import { useEffect, useRef } from "react";
import L from "leaflet";
import "@/lib/leaflet-setup";

interface PickupLocation {
  id: string;
  lat: number;
  lng: number;
  orderNumber: string;
  cemeteryName: string;
  cemeteryCity: string;
}

interface PickupsMapProps {
  pickups: PickupLocation[];
  center: { lat: number; lng: number };
  onClaimPickup: (id: string) => void;
  isClaimPending?: boolean;
}

export function PickupsMap({ pickups, center, onClaimPickup, isClaimPending }: PickupsMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map
    const map = L.map(mapRef.current).setView([center.lat, center.lng], 8);
    mapInstanceRef.current = map;

    // Add tile layer
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [center.lat, center.lng]);

  // Update markers when pickups change
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove existing markers (except tile layers)
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker) {
        map.removeLayer(layer);
      }
    });

    // Create custom red icon for pickups
    const pickupIcon = L.divIcon({
      className: "custom-pickup-marker",
      html: `<div style="
        background-color: #ef4444;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 22v-5"/>
          <path d="M9 8V2"/>
          <path d="M15 8V2"/>
          <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"/>
        </svg>
      </div>`,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16],
    });

    // Add markers for each pickup
    pickups.forEach((pickup) => {
      const marker = L.marker([pickup.lat, pickup.lng], { icon: pickupIcon }).addTo(map);
      
      marker.bindPopup(`
        <div style="min-width: 150px;">
          <div style="font-weight: 600; margin-bottom: 4px;">${pickup.orderNumber}</div>
          <div style="color: #6b7280; font-size: 12px; margin-bottom: 8px;">
            ${pickup.cemeteryName}, ${pickup.cemeteryCity}
          </div>
          <button 
            id="claim-${pickup.id}" 
            style="
              width: 100%;
              padding: 6px 12px;
              background: hsl(var(--primary));
              color: hsl(var(--primary-foreground));
              border: none;
              border-radius: 6px;
              font-size: 12px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 4px;
            "
            ${isClaimPending ? 'disabled' : ''}
          >
            ✋ Ota tehtäväksi
          </button>
        </div>
      `);

      marker.on("popupopen", () => {
        const btn = document.getElementById(`claim-${pickup.id}`);
        if (btn) {
          btn.onclick = () => {
            onClaimPickup(pickup.id);
            marker.closePopup();
          };
        }
      });
    });

    // Fit bounds if there are pickups
    if (pickups.length > 0) {
      const bounds = L.latLngBounds(pickups.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
    }
  }, [pickups, onClaimPickup, isClaimPending]);

  return <div ref={mapRef} className="h-full w-full isolate" />;
}
